import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AnimationLinksSection from "@/components/AnimationLinksSection";
import ChateauformSection from "@/components/ChateauformSection";
import {
  Settings,
  Mail,
  Edit2,
  Trash2,
  Save,
  X,
  Loader2,
  FileText,
  History,
  Users,
  UserPlus,
  Shield,
  Link2,
  MapPin,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SettingsPage = ({ apiUrl, onClose, user }) => {
  const [templates, setTemplates] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(null);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'chef_projet' });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tmplRes, logsRes, usersRes] = await Promise.all([
        axios.get(`${apiUrl}/email-templates`),
        isAdmin ? axios.get(`${apiUrl}/audit-logs?limit=30`) : Promise.resolve({ data: { logs: [] } }),
        isAdmin ? axios.get(`${apiUrl}/admin/users`) : Promise.resolve({ data: [] })
      ]);
      setTemplates(tmplRes.data);
      setAuditLogs(logsRes.data.logs || []);
      setUsers(usersRes.data || []);
    } catch (e) {
      console.error("Error loading settings:", e);
    } finally {
      setLoading(false);
    }
  };

  // User management
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Tous les champs sont obligatoires");
      return;
    }
    try {
      await axios.post(`${apiUrl}/admin/users`, newUser);
      toast.success(`Utilisateur ${newUser.name} cree`);
      setNewUser({ email: '', password: '', name: '', role: 'chef_projet' });
      setShowNewUser(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la creation");
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(`${apiUrl}/admin/users/${userId}/role`, { role: newRole });
      toast.success("Role mis a jour");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${apiUrl}/admin/users/${userId}`);
      toast.success("Utilisateur supprime");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleUpdateTemplate = async (template) => {
    try {
      await axios.put(`${apiUrl}/email-templates/${template.id}`, {
        name: template.name,
        category: template.category,
        subject: template.subject,
        body: template.body,
        variables: template.variables
      });
      toast.success("Modèle mis à jour");
      setEditingTemplate(null);
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${apiUrl}/email-templates/${templateId}`);
      toast.success("Modèle supprimé");
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCreateTemplate = async (templateDraft) => {
    // Auto-detect placeholders {{name}} from body + subject so the backend
    // can echo them back for downstream rendering.
    const fullText = `${templateDraft.subject || ""} ${templateDraft.body || ""}`;
    const variables = Array.from(
      new Set([...fullText.matchAll(/\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi)].map((m) => m[1]))
    );
    try {
      await axios.post(`${apiUrl}/email-templates`, {
        name: templateDraft.name?.trim() || "Sans nom",
        category: templateDraft.category?.trim() || "custom",
        subject: templateDraft.subject || "",
        body: templateDraft.body || "",
        variables,
      });
      toast.success("Modèle créé");
      setCreatingTemplate(null);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-600" />
          <h2 className="text-sm font-semibold text-slate-900">Réglages</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-7 w-7 p-0"
          data-testid="close-settings-btn"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-4">
        <Accordion type="multiple" defaultValue={["templates", "audit", "users"]} className="space-y-2">
          {/* Animation Links Section — local URL store for the "Fiches animations" email template */}
          <AccordionItem value="animation-links" className="border rounded-md">
            <AccordionTrigger className="px-3 py-2 hover:no-underline">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium">Liens fiches animation</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <AnimationLinksSection apiUrl={apiUrl} />
            </AccordionContent>
          </AccordionItem>

          {/* Chateauform addresses — venue address book for the special client */}
          <AccordionItem value="chateauform" className="border rounded-md">
            <AccordionTrigger className="px-3 py-2 hover:no-underline">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium">Adresses Chateauform</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <ChateauformSection apiUrl={apiUrl} />
            </AccordionContent>
          </AccordionItem>

          {/* Email Templates Section */}
          <AccordionItem value="templates" className="border rounded-md">
            <AccordionTrigger className="px-3 py-2 hover:no-underline">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium">Modèles d'email ({templates.length})</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <p className="text-xs text-slate-500 mb-3">
                Configurez les modèles de réponse email.
              </p>

              {isAdmin && (
                <Dialog
                  open={!!creatingTemplate}
                  onOpenChange={(open) => setCreatingTemplate(open ? { name: "", category: "", subject: "", body: "" } : null)}
                >
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs mb-3 w-full"
                      data-testid="create-template-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" />
                      Créer un nouveau modèle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-sm">Nouveau modèle d'email</DialogTitle>
                      <DialogDescription className="text-xs">
                        Renseignez le nom, la catégorie, l'objet et le corps du message.
                      </DialogDescription>
                    </DialogHeader>
                    {creatingTemplate && (
                      <TemplateEditForm
                        template={creatingTemplate}
                        onChange={setCreatingTemplate}
                        onSave={() => handleCreateTemplate(creatingTemplate)}
                        onCancel={() => setCreatingTemplate(null)}
                      />
                    )}
                  </DialogContent>
                </Dialog>
              )}
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {templates.map((template) => (
                  <div 
                    key={template.id}
                    className="flex items-center justify-between p-2 bg-slate-50 rounded-md"
                    data-testid={`template-row-${template.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{template.name}</p>
                      <p className="text-[10px] text-slate-500 truncate">{template.subject}</p>
                    </div>
                    
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => setEditingTemplate({...template})}
                              data-testid={`edit-template-${template.id}`}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-sm">Modifier le modèle</DialogTitle>
                              <DialogDescription className="text-xs">
                                Mettez à jour les informations de ce modèle d'email.
                              </DialogDescription>
                            </DialogHeader>
                            {editingTemplate && (
                              <TemplateEditForm
                                template={editingTemplate}
                                onChange={setEditingTemplate}
                                onSave={() => handleUpdateTemplate(editingTemplate)}
                                onCancel={() => setEditingTemplate(null)}
                              />
                            )}
                          </DialogContent>
                        </Dialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-template-${template.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer ce modèle ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible. Le modèle « {template.name} » sera définitivement supprimé.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`cancel-delete-template-${template.id}`}>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid={`confirm-delete-template-${template.id}`}
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Audit Log Section */}
          {isAdmin && auditLogs.length > 0 && (
            <AccordionItem value="audit" className="border rounded-md">
              <AccordionTrigger className="px-3 py-2 hover:no-underline">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium">Historique ({auditLogs.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <p className="text-xs text-slate-500 mb-3">
                  Dernieres modifications sur les animations et modeles.
                </p>
                <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="audit-log-list">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-2 p-2 bg-slate-50 rounded text-[11px]">
                      <span className={`inline-block mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                        log.action === 'create' ? 'bg-emerald-500' :
                        log.action === 'delete' ? 'bg-rose-500' :
                        log.action === 'update' ? 'bg-blue-500' :
                        log.action.includes('password') ? 'bg-amber-500' :
                        'bg-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-700">
                          <span className="font-medium">{log.user_name || log.user_email || 'Systeme'}</span>
                          {' '}
                          <span className="text-slate-500">
                            {log.action === 'create' ? 'a cree' :
                             log.action === 'delete' ? 'a supprime' :
                             log.action === 'update' ? 'a modifie' :
                             log.action === 'update_link' ? 'a mis a jour le lien de' :
                             log.action === 'password_reset_request' ? 'a demande un reset de mot de passe' :
                             log.action === 'password_reset' ? 'a reinitialise le mot de passe' :
                             log.action}
                          </span>
                          {log.entity_name && (
                            <span className="font-medium"> {log.entity_name}</span>
                          )}
                          <span className="text-slate-400"> ({log.entity_type})</span>
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {new Date(log.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Users Management Section (Admin only) */}
          {isAdmin && (
            <AccordionItem value="users" className="border rounded-md">
              <AccordionTrigger className="px-3 py-2 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium">Utilisateurs ({users.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-500">Gestion des comptes et des roles.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewUser(!showNewUser)}
                    className="h-6 px-2 text-[10px]"
                    data-testid="add-user-btn"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    Nouveau
                  </Button>
                </div>

                {/* New user form */}
                {showNewUser && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md mb-3 space-y-2" data-testid="new-user-form">
                    <Input
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                      className="h-7 text-xs"
                      placeholder="Nom complet"
                      data-testid="new-user-name"
                    />
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                      className="h-7 text-xs"
                      placeholder="Email"
                      data-testid="new-user-email"
                    />
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                      className="h-7 text-xs"
                      placeholder="Mot de passe"
                      data-testid="new-user-password"
                    />
                    <Select value={newUser.role} onValueChange={(val) => setNewUser(prev => ({ ...prev, role: val }))}>
                      <SelectTrigger className="h-7 text-xs" data-testid="new-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="chef_projet">Chef de projet</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button onClick={handleCreateUser} size="sm" className="h-7 text-xs flex-1 bg-slate-900 hover:bg-slate-800" data-testid="confirm-create-user-btn">
                        <Save className="w-3 h-3 mr-1" /> Creer
                      </Button>
                      <Button onClick={() => setShowNewUser(false)} variant="outline" size="sm" className="h-7 text-xs">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Users list */}
                <div className="space-y-1.5" data-testid="users-list">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs" data-testid={`user-${u.id}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.role === 'admin' ? 'bg-violet-500' : 'bg-blue-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 truncate">{u.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{u.email}</p>
                      </div>
                      <Select
                        value={u.role}
                        onValueChange={(val) => handleUpdateUserRole(u.id, val)}
                        disabled={u.email === user?.email}
                      >
                        <SelectTrigger className="h-6 w-28 text-[10px]" data-testid={`user-role-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin" className="text-xs">Admin</SelectItem>
                          <SelectItem value="chef_projet" className="text-xs">Chef de projet</SelectItem>
                        </SelectContent>
                      </Select>
                      {u.totp_enabled && (
                        <Shield className="w-3 h-3 text-emerald-500" title="2FA active" />
                      )}
                      {u.email !== user?.email && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="text-slate-400 hover:text-rose-500 transition-colors"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible. Le compte « {u.name} » ({u.email}) sera définitivement supprimé.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`cancel-delete-user-${u.id}`}>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(u.id, u.name)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid={`confirm-delete-user-${u.id}`}
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        {!isAdmin && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-700">
              Seuls les administrateurs peuvent modifier les réglages.
            </p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// Template Edit Form
const TemplateEditForm = ({ template, onChange, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs">Nom</Label>
        <Input
          value={template.name}
          onChange={(e) => onChange({...template, name: e.target.value})}
          className="h-8 text-sm"
        />
      </div>
      <div>
        <Label className="text-xs">Catégorie</Label>
        <Select
          value={template.category || "custom"}
          onValueChange={(val) => onChange({ ...template, category: val })}
        >
          <SelectTrigger className="h-8 text-sm" data-testid="template-category-select">
            <SelectValue placeholder="Choisir une catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="acknowledgment">Accusé de réception</SelectItem>
            <SelectItem value="clarification">Demande de précisions</SelectItem>
            <SelectItem value="quote">Envoi de devis</SelectItem>
            <SelectItem value="followup">Relance</SelectItem>
            <SelectItem value="appointment">Prise de rendez-vous</SelectItem>
            <SelectItem value="custom">Autre / personnalisé</SelectItem>
            <SelectItem value="animation_info">Fiches animations (spécial)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[10px] text-slate-500 mt-1">
          La catégorie sert uniquement à classer le modèle dans la liste. Pour un modèle classique
          avec variables, choisissez « Autre / personnalisé ». N'utilisez « Fiches animations » que
          pour le modèle de liens d'animations.
        </p>
      </div>
      <div>
        <Label className="text-xs">Objet</Label>
        <Input
          value={template.subject}
          onChange={(e) => onChange({...template, subject: e.target.value})}
          className="h-8 text-sm"
        />
      </div>
      <div>
        <Label className="text-xs">Corps du message</Label>
        <Textarea
          value={template.body}
          onChange={(e) => onChange({...template, body: e.target.value})}
          className="text-sm min-h-[150px] font-mono"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Variables disponibles: {"{{"}"first_name{"}}"}, {"{{"}"company{"}}"}, {"{{"}"event_date{"}}"}, {"{{"}"animation_links{"}}"}, {"{{"}"lien_devis{"}}"}...
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={onCancel}
          variant="outline"
          className="flex-1 h-8 text-xs"
        >
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 h-8 text-xs bg-slate-900 hover:bg-slate-800"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
